import React from 'react';
import { Button, Card, List, Space, Tag, Timeline, Typography, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { OiEmptyState, OiPageFrame } from '@/components/owner-intelligence/OwnerIntelligenceComponents';
import { automationService } from '@/services/automation.service';

const { Title, Text, Paragraph } = Typography;

const AutomationExecutionPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['eabp-execution', id],
    queryFn: () => automationService.getExecution(id!),
    enabled: !!id,
  });

  const resumeMut = useMutation({
    mutationFn: () => automationService.resume(id!, { source: 'ui' }),
    onSuccess: () => {
      message.success('Execution resumed');
      void qc.invalidateQueries({ queryKey: ['eabp-execution', id] });
      void qc.invalidateQueries({ queryKey: ['eabp-monitor'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const data = q.data;
  const execution = (data?.execution || {}) as Record<string, unknown>;
  const steps = (data?.steps as any[]) || [];
  const nodes = (data?.nodes as any[]) || [];

  return (
    <OiPageFrame
      subtitle="Execution graph — orchestration timeline. Resume human waits; approvals remain in the universal approval inbox."
      loading={q.isLoading && !data}
      error={Boolean(q.error)}
      errorMessage="Execution unavailable"
    >
      <Space style={{ marginBottom: 12 }}>
        <Button onClick={() => navigate('/owner-intelligence/automation')}>Back</Button>
        {execution.status === 'waiting' ? (
          <Button type="primary" loading={resumeMut.isPending} onClick={() => resumeMut.mutate()}>
            Resume (human wait)
          </Button>
        ) : null}
      </Space>

      {!data ? (
        <OiEmptyState title="Not found" body="Execution missing or access denied." />
      ) : (
        <>
          <Title level={3} style={{ marginTop: 0 }}>
            Execution {String(execution.id || '').slice(0, 8)}
          </Title>
          <Paragraph>
            Status <Tag>{String(execution.status)}</Tag>
            {execution.current_node_key ? (
              <>
                {' '}
                · node <Text code>{String(execution.current_node_key)}</Text>
              </>
            ) : null}
          </Paragraph>
          {execution.last_error ? (
            <Paragraph type="danger">{String(execution.last_error)}</Paragraph>
          ) : null}

          <Card title="Timeline" style={{ marginBottom: 16 }}>
            <Timeline
              items={steps.map((s) => ({
                color:
                  s.status === 'failed'
                    ? 'red'
                    : s.status === 'waiting'
                      ? 'orange'
                      : s.status === 'succeeded'
                        ? 'green'
                        : 'blue',
                children: (
                  <span>
                    <Tag>{s.node_kind}</Tag> {s.node_key} — {s.status}
                  </span>
                ),
              }))}
            />
          </Card>

          <Card title="Graph nodes">
            <List
              size="small"
              dataSource={nodes}
              renderItem={(n) => (
                <List.Item>
                  <Tag>{n.type}</Tag>
                  <Text>{n.label}</Text> <Tag>{n.status}</Tag>
                </List.Item>
              )}
            />
          </Card>
        </>
      )}
    </OiPageFrame>
  );
};

export default AutomationExecutionPage;
